import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdOrders } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Order } from '~hooks/useOrders/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

interface UseDiscardedOrdersQueryProps {
  companyId?: string;
  cursor?: string;
  direction?: PaginationLink;
  endDate?: string;
  startDate?: string;
}

// The date range filters orders.load_at, not deletion time: discarded_at is not exposed
// on Order_Read.
export default function useDiscardedOrdersQuery({
  companyId,
  cursor,
  direction,
  endDate,
  startDate,
}: UseDiscardedOrdersQueryProps) {
  const query = useQuery({
    queryKey: ['discardedOrders', companyId, direction, cursor, startDate, endDate],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            const response = await getV1CompaniesCompanyIdOrders({
              path: { 'company-id': companyId },
              query: {
                'filter[discarded]': true,
                'page[limit]': MAX_API_PAGE_SIZE,
                ...(direction && cursor ? { [`page[${direction}]`]: cursor } : {}),
                ...(startDate ? { 'filter[start_date]': startDate } : {}),
                ...(endDate ? { 'filter[end_date]': endDate } : {}),
              },
              signal,
            });

            return {
              orders: response.data.data.map(Order.parse),
              pagination: extractPagination(response),
            };
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.orders.failed_to_fetch'),
            );
            throw error;
          }
        }
      : skipToken,
  });

  return {
    isFetching: query.isFetching,
    orders: query.data?.orders ?? [],
    pagination: query.data?.pagination,
  };
}
